import React, { useRef, useState } from "react";

import { mdiBroom, mdiDelete, mdiPencil } from "@mdi/js";
import Icon from "@mdi/react";
import { getTrueItemId } from "okast-timeline";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import ContentTooltipItem from "./ContentTooltipItem";
import { TIME_FORMAT } from "../../../constants/dateFormat";
import { MIN_SIZE_SHOW_LABEL_ITEM_VERTICAL } from "../../../constants/fast";
import { AD_BREAK, TIMESLOT } from "../../../constants/media";
import { getItemRendererStyle, parseCssTextValue } from "../../../utils/fast";
import { BUTTON_VARIANTS } from "../../button/Button";
import ConfirmModal from "../../modal/ConfirmModal";
import { FAST_CHANNELS_ROUTE } from "../../routes/routes";

import type { Moment } from "moment";
import type { JSX } from "react";

import type {
  CustomItemRendererProps,
  TemplateTimelineItem,
} from "./timeline.types";
import type { MediaLibraryDragItem } from "../../../types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface TitleItemProps {
  isVertical: boolean;
  size: number;
  isSelected: boolean;
  setShowModalDeleteItem: (show: boolean) => void;
  startTime: Moment;
  endTime: Moment;
  item: TemplateTimelineItem;
}

export function TitleItem({
  isVertical,
  size,
  isSelected,
  setShowModalDeleteItem,
  startTime,
  endTime,
  item,
}: TitleItemProps): JSX.Element | null {
  const history = useHistory();
  const { channelId } = useParams<FASTChannelRoutesParams>();

  if (isVertical && size < MIN_SIZE_SHOW_LABEL_ITEM_VERTICAL) {
    return null;
  }

  const handleUpdate = (): void => {
    if (item.type === AD_BREAK) {
      history.push(
        `${FAST_CHANNELS_ROUTE}/${channelId}/ad-breaks/${item.mediaId}`,
      );
    }

    if (item.type === TIMESLOT) {
      history.push(
        `${FAST_CHANNELS_ROUTE}/${channelId}/timeslots/${item.mediaId}`,
      );
    }
  };

  const handleDeleteItem = (): void => {
    setShowModalDeleteItem(true);
  };

  const actionButtons = (
    <div className="flex gap-[0.3125rem]">
      {isSelected ? (
        <React.Fragment>
          <div onClick={handleUpdate}>
            <Icon path={mdiPencil} size={0.78125} />
          </div>
          <div onClick={handleDeleteItem}>
            <Icon path={mdiDelete} size={0.78125} />
          </div>
        </React.Fragment>
      ) : null}
    </div>
  );

  if (isVertical && size < 35) {
    return (
      <div className="flex w-full items-center justify-between gap-[0.46875rem] px-[0.46875rem]">
        <span className="truncate font-semibold">{item.title}</span>
        {actionButtons}
      </div>
    );
  }

  return (
    <div className="flex w-full justify-between gap-[0.46875rem] p-[0.46875rem]">
      <div className="flex flex-col overflow-hidden">
        <span className="font-semibold">{item.title}</span>
        <div className="flex gap-[0.3125rem]">
          <span>{startTime.format(TIME_FORMAT)}</span>
          <span>|</span>
          <span>{endTime.format(TIME_FORMAT)}</span>
        </div>
      </div>
      {actionButtons}
    </div>
  );
}

function TemplateTimelineItemRenderer({
  dropzone,
  isDraggingMediaLibraryItem,
  itemProps,
  item,
  showModalReplaceItem,
  itemContext,
  isVerticalTimeline,
  dropMedia,
  onReplaceItem,
  isReplaceMode,
  setShowModalReplaceItem,
  onDeleteItem,
  isLoopTimeline,
}: CustomItemRendererProps<TemplateTimelineItem>): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const [showModalDeleteItem, setShowModalDeleteItem] = useState(false);

  const dropRef = useRef<HTMLDivElement>(null);

  const { dropRef: dropConnector, canDrop, isOver } = dropzone;

  dropConnector(dropRef);

  const {
    ref,
    style,
    onContextMenu,
    onDoubleClick,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
  } = itemProps;

  const { color: itemColor } = item;

  const { height: itemPropsHeight = 0 } = style;

  const isDragging = itemContext.dragging;
  const isSelected = itemContext.selected;
  const { truncated } = itemContext.dimensions;

  const isDropZone = Boolean(isReplaceMode);

  const showDropZone = isDropZone && isDraggingMediaLibraryItem;

  const formatedItemPropsHeight =
    typeof itemPropsHeight === "string"
      ? parseCssTextValue(itemPropsHeight)
      : itemPropsHeight;

  const styleItemCustom = getItemRendererStyle({
    color: itemColor as string,
    isDragging,
    isSelected,
    showDropZone,
    canDrop,
    isOver,
    isVerticalTimeline,
    truncated,
    canSelect: true,
  });

  const handleReplaceItem = (
    itemToDelete: TemplateTimelineItem,
    itemToAdd: MediaLibraryDragItem | null,
  ): void => {
    if (itemToAdd && onReplaceItem) {
      onReplaceItem(itemToDelete, itemToAdd);
    }
  };

  const confirmModalReplaceItemActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModalReplaceItem(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("replace"),
      onClick: (): void => handleReplaceItem(item, dropMedia),
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
    },
  ];

  const itemId = getTrueItemId(item.id, isLoopTimeline);

  const handleDeleteItem = (): void => {
    if (itemId && onDeleteItem) {
      onDeleteItem(itemId);
    }
  };

  const confirmModalDeleteItemActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModalReplaceItem(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("delete"),
      onClick: (): void => handleDeleteItem(),
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
    },
  ];

  const startTime = item.splitedItem?.trueStartTime ?? item.start_time;
  const endTime = item.splitedItem?.trueEndTime ?? item.end_time;

  return (
    <React.Fragment>
      <div
        ref={ref}
        data-tooltip-id="timeline-tooltip"
        data-tooltip-place="bottom"
        onContextMenu={onContextMenu}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
        onTouchStart={onTouchStart}
        style={styleItemCustom}
        data-tooltip-html={renderToStaticMarkup(
          <ContentTooltipItem
            duration={item.duration}
            endTime={endTime}
            startTime={startTime}
            title={itemContext.title}
          />,
        )}
      >
        <div ref={isDropZone ? dropRef : null} className="flex size-full">
          <TitleItem
            endTime={endTime}
            isSelected={isSelected}
            isVertical={isVerticalTimeline}
            item={item}
            setShowModalDeleteItem={setShowModalDeleteItem}
            size={formatedItemPropsHeight}
            startTime={startTime}
          />
        </div>
      </div>
      <ConfirmModal
        actions={confirmModalReplaceItemActions}
        iconPath={mdiBroom}
        isOpen={showModalReplaceItem}
        onClose={(): void => setShowModalReplaceItem(false)}
        size="sm"
        title={t("replaceThisElement")}
      >
        {t("replaceThisElementConfirmation")}
      </ConfirmModal>
      <ConfirmModal
        actions={confirmModalDeleteItemActions}
        iconPath={mdiBroom}
        isOpen={showModalDeleteItem}
        onClose={(): void => setShowModalDeleteItem(false)}
        size="sm"
        title={t("deleteThisItem")}
      >
        {t("deleteItemTemplateConfirmation")}
      </ConfirmModal>
    </React.Fragment>
  );
}

export default TemplateTimelineItemRenderer;
